import { Header, HeaderGroup, flexRender } from '@tanstack/react-table';
import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SparkelIcon from '../../../common/icon/SparkelIcon';
import { OrderEntryTableRow } from './columns';

type HeaderProps = {
  header: Header<OrderEntryTableRow, unknown>;
  headerGroup: HeaderGroup<OrderEntryTableRow>;
  idx: number;
  isRenaming?: boolean;
};

type AggregateOrderTableColumnMeta = {
  menuItems: React.ReactNode | null;
  isAction?: boolean;
  isNumeric?: boolean;
  name?: string;
  columnId?: string;
  originalKey?: string;
  canHide?: boolean;
  canRename?: boolean;
  originalId?: string;
};

// eslint-disable-next-line complexity
export default memo(function AggregatedOrderTableHeader({
  header,
  headerGroup,
  idx,
  isRenaming,
}: HeaderProps) {
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { t } = useTranslation('table');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const columnMeta = (header.column.columnDef.meta ??
    {}) as AggregateOrderTableColumnMeta;
  const hasMenu = header.column.getCanSort() || columnMeta.menuItems;

  const displayHeaderIds = ['select', 'expander', 'linkedCount', 'color'];

  // Close the menu if we're in rename mode
  useEffect(() => {
    if (isRenaming) {
      onClose();
    }
  }, [isRenaming, onClose]);

  // In case this column's header is a React element that includes ColumnHeader component,
  // we need to ensure it gets the isRenaming and onRename props correctly
  const headerContent = () => {
    // Access the original header content
    const originalHeader = flexRender(
      header.column.columnDef.header,
      header.getContext()
    );

    // If we're in rename mode, we need to inject the props
    // This is a simplified check - the actual column headers that support renaming
    // should be already configured in columns.tsx
    return originalHeader;
  };

  return (
    <Flex
      role="columnheader"
      direction={'row'}
      position={'relative'}
      align={'center'}
      style={{
        width: `calc(var(--header-${header.id}-size) * 1px)`,
      }}
      padding={1}
      overflow={'hidden'}
      cursor={displayHeaderIds.includes(header.id) ? 'default' : 'pointer'}
      _hover={{
        backgroundColor: displayHeaderIds.includes(header.id)
          ? 'none'
          : hoverBackgroundColor,
      }}
    >
      {hasMenu ? (
        <Menu isOpen={isOpen && !isRenaming} onClose={onClose}>
          <MenuButton
            width={'100%'}
            top={header.id === 'select' ? -0.5 : 0}
            onClick={(e) => {
              if (isRenaming) {
                // Prevent menu from opening during rename
                e.preventDefault();
                e.stopPropagation();
              } else {
                onOpen();
              }
            }}
          >
            {headerContent()}
          </MenuButton>
          <Portal>
            <MenuList zIndex={99}>
              {header.column.getCanSort() ? (
                <MenuGroup>
                  <MenuItem
                    onClick={() => header.column.toggleSorting(false)}
                    icon={<SparkelIcon icon={faArrowUp} fixedWidth />}
                  >
                    {t('column.sort-ascending')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => header.column.toggleSorting(true)}
                    icon={<SparkelIcon icon={faArrowDown} fixedWidth />}
                  >
                    {t('column.sort-descending')}
                  </MenuItem>
                </MenuGroup>
              ) : null}

              {columnMeta.menuItems}
            </MenuList>
          </Portal>
        </Menu>
      ) : (
        headerContent()
      )}

      {idx !== headerGroup.headers.length - 1 &&
        header.column.columnDef.id !== 'expander' && (
          <Resizer header={header} color={borderColor} />
        )}
    </Flex>
  );
});

type ResizerProps = {
  header: Header<OrderEntryTableRow, unknown>;
  color: string;
};

const Resizer = ({ header, color }: ResizerProps) => {
  return (
    <Box
      position={'absolute'}
      right={0}
      borderLeft={'1.5px solid'}
      borderColor={color}
      width={'4px'}
      height={'20px'}
      padding={0}
      margin={0}
      cursor={header.column.getCanResize() ? 'col-resize' : 'default'}
      onMouseDown={
        header.column.getCanResize() ? header.getResizeHandler() : undefined
      }
      _active={{
        backgroundColor: color,
        borderRadius: '2px',
      }}
      userSelect={'none'}
    />
  );
};
